/* eslint-disable no-console */
import { IAnalyticEvent } from './events/analytics';
import { AmplitudeProvider } from './providers/amplitude';
import { IntercomProvider } from './providers/intercom';
import { HubSpotProvider } from './providers/hubspot';
import { GTMProvider } from './providers/gtm';

export interface TrackProps {
  orgId?: string;
  userId?: string;
  eventId?: string;
  alreadyKnown?: boolean;
  [key: string]: any;
}

export interface IdentityPayload {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  properties?: Record<string, any>;
}

export interface InitOptions {
  amplitudeApiKey?: string;
  intercomAppId?: string;
  withHubspot?: boolean;
  gtmInstance?: unknown;
  environment: 'development' | 'staging' | 'production';
  debug?: boolean;
}

export interface AnalyticsProvider {
  init(options: any): Promise<void> | void;
  identify(identity: IdentityPayload): void;
  track(event: string, properties?: TrackProps): void;
  isReady(): boolean;
}

export default class AnalyticsService {
  public providers: AnalyticsProvider[] = [];
  private isInitialized = false;
  private currentIdentity: IdentityPayload | null = null;
  private debugMode = false;

  /**
   * Initializes the analytics service with the provided configuration options
   * @param options - Configuration options for various analytics providers
   */
  public init(options: InitOptions): void {
    try {
      this.debugMode = options.debug || false;

      if (options.amplitudeApiKey) {
        const amplitude = new AmplitudeProvider();
        amplitude.init({ apiKey: options.amplitudeApiKey, debug: this.debugMode });
        this.providers.push(amplitude);
      }

      if (options.intercomAppId) {
        const intercom = new IntercomProvider();
        intercom.init({ appId: options.intercomAppId, debug: this.debugMode });
        this.providers.push(intercom);
      }

      if (options.withHubspot) {
        const hubspot = new HubSpotProvider();
        hubspot.init({ debug: this.debugMode });
        this.providers.push(hubspot);
      }

      if (options.gtmInstance) {
        const gtm = new GTMProvider();
        gtm.init({ gtmInstance: options.gtmInstance, debug: this.debugMode });
        this.providers.push(gtm);
      }

      this.isInitialized = true;

      if (this.debugMode) console.debug('Analytics service initialized with providers:', this.providers.length);
    } catch (error) {
      this.isInitialized = true;
    }
  }

  /**
   * Identifies a user across all analytics providers
   * @param identity - User identity information including userId, email, and optional name
   */
  public identify(identity: IdentityPayload): void {
    if (!this.isInitialized) return;

    try {
      this.currentIdentity = identity;
      this.providers.filter(provider => provider.isReady()).forEach(provider => provider.identify(identity));
      if (this.debugMode) console.debug('Analytics: User identified:', identity.userId);
    } catch (error) {
      console.error('Analytics identify failed:', error);
    }
  }

  /**
   * Track an analytics event
   * @param event - Event class instance implementing IAnalyticEvent interface
   */
  public track(event: IAnalyticEvent): void {
    if (!this.isInitialized) return;

    try {
      if (this.currentIdentity) event.properties.userId = event.properties.userId || this.currentIdentity.userId;

      this.providers
        .filter(provider => provider.isReady())
        .forEach(provider => provider.track(event.name, event.properties));

      if (this.debugMode) console.debug('Analytics: Event tracked:', event.name, event.properties);
    } catch (error) {
      console.error('Analytics track failed:', error);
    }
  }

  /**
   * Checks if the analytics service is initialized
   * @returns true if initialized, false otherwise
   */
  public isReady(): boolean {
    return this.isInitialized;
  }
}
